/* ---------------------------------------------------------------------------
   Which way a Kabbalah board travels
   ---------------------------------------------------------------------------
   The Kabbalah board is organization-owned — one screen, one artboard, chosen
   once — so its direction is chosen in the Kabbalah settings modal rather than
   per artboard, and that choice wins while the board is the element on screen.

   Null until an organization picks one, which is what lets a board that has
   never been through the modal keep following its artboard's own setting.
   -------------------------------------------------------------------------- */

export type KabbalahCarouselDirection = 'horizontal' | 'vertical';

export interface KabbalahDirectionOption {
  value: KabbalahCarouselDirection;
  label: string;
  hint: string;
  icon: string;
}

export const KABBALAH_DIRECTION_OPTIONS: KabbalahDirectionOption[] = [
  {
    value: 'horizontal',
    label: 'Horizontal',
    hint: 'Kabbalos slide across, right to left.',
    icon: 'pi-arrow-left',
  },
  {
    value: 'vertical',
    label: 'Vertical',
    hint: 'Kabbalos move upward, one row above the next.',
    icon: 'pi-arrow-up',
  },
];

/**
 * Null for anything that is not one of the two directions — including the empty
 * value an organization that has never chosen one stores. Callers read that
 * null as "no choice made", not as a direction.
 */
export const normalizeKabbalahCarouselDirection = (
  value: unknown
): KabbalahCarouselDirection | null => {
  const direction = String(value ?? '').trim().toLowerCase();
  return direction === 'horizontal' || direction === 'vertical' ? direction : null;
};

/** The organization's chosen direction, or null while it has none. */
export const getKabbalahCarouselDirection = (
  organization: any
): KabbalahCarouselDirection | null =>
  normalizeKabbalahCarouselDirection(organization?.kabbalahCarouselDirection);
